import React from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Send, Lock, RotateCcw, X, Sparkles, HelpCircle, MousePointer2, Wand2 } from 'lucide-react';
import SupportPanel from '@/components/SupportPanel';
import ContentTailoringPanel from '@/components/ContentTailoringPanel';

/**
 * PreviewChatPanel - Floating chat panel for design revisions and support
 * Extracted from DesignPreviewPage.js to reduce file size
 */
const PreviewChatPanel = ({
  // State
  chatOpen,
  setChatOpen,
  sidePanelTab,
  setSidePanelTab,
  revisionMode,
  setRevisionMode,
  revisionPrompt,
  setRevisionPrompt,
  revising,
  advancedProgress,
  credits,
  selectedElement,
  clearElementSelection,
  // Project data
  project,
  projectId,
  currentDesign,
  // Handlers
  handleReviseDesign,
  onContentUpdated,
}) => {
  if (!chatOpen) {
    return null;
  }

  const isFrozen = project?.status === 'frozen' || project?.status === 'pending_review';

  return (
    <div 
      className="fixed bottom-6 right-6 w-[400px] h-[600px] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col z-50 overflow-hidden"
      data-testid="preview-chat-panel"
    >
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-white" />
          <span className="font-semibold text-white">AI Design Assistant</span>
        </div>
        <button
          onClick={() => setChatOpen(false)}
          className="p-1 hover:bg-white/20 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>
      
      {/* Chat Tabs */}
      <div className="flex border-b border-zinc-200">
        <button
          onClick={() => setSidePanelTab('tailor')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            sidePanelTab === 'tailor'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-zinc-500 hover:text-zinc-700'
          }`}
          data-testid="tailor-tab"
        >
          <Wand2 className="w-4 h-4" />
          Tailor
        </button>
        <button
          onClick={() => setSidePanelTab('revisions')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            sidePanelTab === 'revisions'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-zinc-500 hover:text-zinc-700'
          }`}
          data-testid="revisions-tab"
        >
          <RotateCcw className="w-4 h-4" />
          Revise
        </button>
        <button
          onClick={() => setSidePanelTab('support')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            sidePanelTab === 'support'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-zinc-500 hover:text-zinc-700'
          }`}
          data-testid="support-tab"
        >
          <HelpCircle className="w-4 h-4" />
          Help
        </button>
      </div>
      
      {/* Chat Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {sidePanelTab === 'tailor' ? (
          <ContentTailoringPanel
            designId={currentDesign?.design_id}
            projectId={projectId}
            onContentUpdated={onContentUpdated}
          />
        ) : sidePanelTab === 'revisions' ? (
          <>
            {isFrozen ? (
              <FrozenDesignMessage />
            ) : (
              <RevisionContent
                revisionMode={revisionMode}
                setRevisionMode={setRevisionMode}
                revisionPrompt={revisionPrompt}
                setRevisionPrompt={setRevisionPrompt}
                revising={revising}
                advancedProgress={advancedProgress}
                credits={credits}
                selectedElement={selectedElement}
                clearElementSelection={clearElementSelection}
                handleReviseDesign={handleReviseDesign}
              />
            )}
          </>
        ) : (
          <SupportPanel
            projectId={projectId}
            project={project}
            currentDesign={currentDesign}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Frozen Design Message
 */
const FrozenDesignMessage = () => (
  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
    <div className="flex items-center gap-2 mb-2">
      <Lock className="w-5 h-5 text-green-600" />
      <span className="font-semibold text-green-900">Design Frozen</span>
    </div>
    <p className="text-sm text-green-700">
      Your design has been finalized. Our team is building your website!
    </p>
  </div>
);

/**
 * Revision Content - Quick and Advanced modes
 */
const RevisionContent = ({
  revisionMode,
  setRevisionMode,
  revisionPrompt,
  setRevisionPrompt,
  revising,
  advancedProgress,
  credits,
  selectedElement,
  clearElementSelection,
  handleReviseDesign,
}) => (
  <>
    {/* Revision Mode Tabs */}
    <div className="flex gap-2 mb-4">
      <button
        onClick={() => setRevisionMode('quick')}
        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
          revisionMode === 'quick'
            ? 'bg-indigo-600 text-white'
            : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
        }`}
      >
        ⚡ Quick
      </button>
      <button
        onClick={() => setRevisionMode('advanced')}
        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
          revisionMode === 'advanced'
            ? 'bg-indigo-600 text-white'
            : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
        }`}
      >
        🎨 Advanced
      </button>
    </div>

    {revisionMode === 'quick' ? (
      <QuickRevisionMode
        revisionPrompt={revisionPrompt}
        setRevisionPrompt={setRevisionPrompt}
        revising={revising}
        credits={credits}
        selectedElement={selectedElement}
        clearElementSelection={clearElementSelection}
        handleReviseDesign={handleReviseDesign}
      />
    ) : (
      <AdvancedRevisionMode
        revisionPrompt={revisionPrompt}
        setRevisionPrompt={setRevisionPrompt}
        revising={revising}
        advancedProgress={advancedProgress}
        credits={credits}
        handleReviseDesign={handleReviseDesign}
      />
    )}
    
    <div className="pt-4 border-t">
      <p className="text-xs text-zinc-500 text-center">
        💡 Tip: {selectedElement 
          ? 'Describe what you want to change about the selected element' 
          : 'Use "Select Element" button to click and edit specific parts of your design'}
      </p>
    </div>
  </>
);

/**
 * Quick Revision Mode
 */
const QuickRevisionMode = ({
  revisionPrompt,
  setRevisionPrompt,
  revising,
  credits,
  selectedElement,
  clearElementSelection,
  handleReviseDesign,
}) => (
  <>
    {/* Selected Element Context */}
    {selectedElement && (
      <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid="selected-element-context">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <MousePointer2 className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">Editing Selected Element</span>
          </div>
          <button
            onClick={clearElementSelection}
            className="p-1 hover:bg-purple-200 rounded-full transition-colors"
          >
            <X className="w-3 h-3 text-purple-600" />
          </button>
        </div>
        <div className="flex gap-2 mb-2">
          <span className="px-2 py-0.5 bg-purple-200 text-purple-700 text-xs font-medium rounded">
            {selectedElement.sectionType}
          </span>
          <span className="px-2 py-0.5 bg-zinc-200 text-zinc-600 text-xs font-medium rounded">
            {selectedElement.elementType}
          </span>
        </div>
        <p className="text-xs text-purple-700 line-clamp-2">
          {selectedElement.currentText?.substring(0, 80)}
          {selectedElement.currentText?.length > 80 && '...'}
        </p>
      </div>
    )}
    <Textarea
      value={revisionPrompt}
      onChange={(e) => setRevisionPrompt(e.target.value)}
      placeholder="Describe what you'd like to change..."
      className="min-h-[120px] resize-none text-sm"
      data-testid="revision-input"
    />
    <Button
      onClick={handleReviseDesign}
      disabled={!revisionPrompt.trim() || revising || credits < 0.5}
      className="w-full bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center gap-2"
      data-testid="submit-revision-btn"
    >
      {revising ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <Send className="w-4 h-4" />
          Request Revision (0.5 credits)
        </>
      )}
    </Button>
  </>
);

/**
 * Advanced Revision Mode
 */
const AdvancedRevisionMode = ({
  revisionPrompt,
  setRevisionPrompt,
  revising,
  advancedProgress,
  credits,
  handleReviseDesign,
}) => (
  <>
    <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-indigo-600" />
        <span className="font-semibold text-indigo-900">AI-Powered Redesign</span>
      </div>
      <p className="text-sm text-indigo-700 mb-4">
        Get a completely new design variation with advanced AI analysis and creative generation.
      </p>
      
      <Textarea
        value={revisionPrompt}
        onChange={(e) => setRevisionPrompt(e.target.value)}
        placeholder="Optional: Describe specific changes or leave blank for AI to decide..."
        className="min-h-[80px] resize-none text-sm mb-3 bg-white"
        data-testid="advanced-revision-input"
      />
      
      {advancedProgress && (
        <div className="mb-3 p-3 bg-white rounded-lg border border-indigo-200">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
            <span className="text-sm text-indigo-700">{advancedProgress}</span>
          </div>
        </div>
      )}
      
      <Button
        onClick={handleReviseDesign}
        disabled={revising || credits < 2}
        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 flex items-center justify-center gap-2"
        data-testid="advanced-revision-btn"
      >
        {revising ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            Generate New Design (2 credits)
          </>
        )}
      </Button>
    </div>
    
    <div className="text-xs text-zinc-500 text-center">
      Advanced revisions use AI to analyze your current design and generate fresh creative variations.
    </div>
  </>
);

export default PreviewChatPanel;
